import { Link, useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  ColorModeToggle,
  Container,
  OtpInput,
  PhoneInput,
  Stepper,
  Stack,
  Text,
  TextField,
  Typography,
} from '@fse/ui';
import { useMemo, useState, type FormEvent } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError, readCurrentRole } from '../api/client';
import {
  authControllerRequestOtp,
  authControllerVerifyOtp,
  authControllerClaim,
} from '../api/generated/auth/auth';

/**
 * Public "Claim your account" flow (D-021 Path A — web).
 *
 * When a cooperative admin pre-registers a farmer, the backend creates a
 * `users` row with NO pin_hash, so that farmer can't log in. This screen lets
 * them take ownership of the EXISTING (pin-less) account: prove phone
 * possession via OTP (purpose='claim'), set their own PIN, and get logged in.
 *
 * Three steps, mirroring the mobile claim screen and the web forgot-pin wizard:
 *   1. enter phone        -> POST /auth/otp/request {purpose:'claim'}
 *   2. enter 5-digit OTP  -> POST /auth/otp/verify  {purpose:'claim'}
 *   3. enter new PIN x2   -> POST /auth/claim        -> AuthTokens
 *
 * On the final success the server mints tokens (same shape login returns); we
 * persist them exactly like login.tsx and route by role.
 *
 * Contracts verified against services/api/src/auth/dto.ts (ClaimRequest is
 * { phone, pin, display_name? }; 'claim' is a valid OtpPurpose). A group/coop-
 * minted claimable account starts with a placeholder name (the phone); the
 * claimer optionally sets their real name here so their group sees who they are.
 */

// Schemas are built per-render from t() so their validation messages follow the
// chosen language (TD-088). Memoised on t so the object identity is stable.
function buildSchemas(t: TFunction) {
  // Phone is captured as the 9-digit ZA-local portion (PhoneInput renders the
  // fixed `+27` adornment). E.164 is rebuilt at the API boundary, so the request
  // payload is unchanged. The same i18n message key is preserved.
  const phoneSchema = z.object({
    phone: z.string().regex(/^\d{9}$/, t('claim.validation.phone')),
  });

  const otpSchema = z.object({
    code: z.string().regex(/^\d{5}$/, t('claim.validation.otp')),
  });

  const pinSchema = z
    .object({
      pin: z.string().regex(/^\d{4,8}$/, t('claim.validation.pin')),
      confirmPin: z.string(),
    })
    .refine((v) => v.pin === v.confirmPin, {
      path: ['confirmPin'],
      message: t('claim.validation.pinMismatch'),
    });

  return { phoneSchema, otpSchema, pinSchema };
}

function persistTokens(tokens: { access_token: string; refresh_token: string }) {
  window.localStorage.setItem('fse_access_token', tokens.access_token);
  window.localStorage.setItem('fse_refresh_token', tokens.refresh_token);
}

export function ClaimScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Wizard step labels (resolved per-render so they follow the language).
  const STEPS = [t('claim.step.phone'), t('claim.step.verify'), t('claim.step.setPin')] as const;

  const { phoneSchema, otpSchema, pinSchema } = useMemo(() => buildSchemas(t), [t]);

  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  // Optional real name set at claim time (a claimable account starts with a
  // phone placeholder). Omitted when blank — the existing name is then kept.
  const [displayName, setDisplayName] = useState('');

  const [fieldErrors, setFieldErrors] = useState<{
    phone?: string;
    code?: string;
    pin?: string;
    confirmPin?: string;
  }>({});
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function mapApiError(err: unknown, kind: 'request' | 'verify' | 'claim'): string {
    if (err instanceof FseApiError) {
      if (err.status === 404) {
        return t('claim.apiError.notFound');
      }
      if (err.status === 409) {
        return t('claim.apiError.conflict');
      }
      if (err.status === 401) {
        return kind === 'claim'
          ? t('claim.apiError.expired')
          : t('claim.apiError.badCode');
      }
      if (err.status === 400) {
        return t('claim.apiError.badRequest');
      }
      return err.message;
    }
    return String(err);
  }

  async function onSubmitPhone(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setApiErrorMessage(null);
    const parsed = phoneSchema.safeParse({ phone });
    if (!parsed.success) {
      const msg = parsed.error.issues[0]?.message;
      setFieldErrors({ phone: msg });
      return;
    }
    setPending(true);
    try {
      // Generated orval client (TD-070) + E.164 rebuilt from the 9 local digits (TD-089).
      await authControllerRequestOtp({ phone: `+27${parsed.data.phone}`, purpose: 'claim' });
      setStep(1);
    } catch (err) {
      setApiErrorMessage(mapApiError(err, 'request'));
    } finally {
      setPending(false);
    }
  }

  async function onSubmitOtp(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setApiErrorMessage(null);
    const parsed = otpSchema.safeParse({ code });
    if (!parsed.success) {
      const msg = parsed.error.issues[0]?.message;
      setFieldErrors({ code: msg });
      return;
    }
    setPending(true);
    try {
      await authControllerVerifyOtp({ phone: `+27${phone}`, code: parsed.data.code, purpose: 'claim' });
      setStep(2);
    } catch (err) {
      setApiErrorMessage(mapApiError(err, 'verify'));
    } finally {
      setPending(false);
    }
  }

  async function onSubmitPin(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setApiErrorMessage(null);
    const parsed = pinSchema.safeParse({ pin, confirmPin });
    if (!parsed.success) {
      const errs: { pin?: string; confirmPin?: string } = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === 'pin' || key === 'confirmPin') errs[key] = issue.message;
      }
      setFieldErrors(errs);
      return;
    }
    setPending(true);
    try {
      const tokens = await authControllerClaim({
        phone: `+27${phone}`,
        pin: parsed.data.pin,
        display_name: displayName.trim() || undefined,
      });
      persistTokens(tokens);
      // Route by role: farmers (the expected persona here) land on the
      // farmer home; sysCollab is a kanban-only persona; everyone else gets
      // the dashboard.
      const role = readCurrentRole();
      if (role === 'farmer') {
        void navigate({ to: '/home' });
      } else if (role === 'sysCollab') {
        window.location.assign('/kanban.html');
      } else {
        void navigate({ to: '/dashboard' });
      }
    } catch (err) {
      setApiErrorMessage(mapApiError(err, 'claim'));
    } finally {
      setPending(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ position: 'fixed', top: 8, right: 8 }}>
        <ColorModeToggle />
      </Box>
      <Container maxWidth="xs">
        <Stack spacing={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 1 }}>
              <Text variant="display-small" as="h1" align="center">
                {t('claim.heading')}
              </Text>
            </Box>
            <Text variant="body-large" align="center" color="text.secondary">
              {t('claim.tagline')}
            </Text>
          </Box>

          <Stepper steps={[...STEPS]} activeStep={step} />

          <Card>
            <CardContent>
              {step === 0 && (
                <form onSubmit={onSubmitPhone} noValidate>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('claim.phone.body')}
                    </Typography>
                    <PhoneInput
                      label={t('claim.phone.label')}
                      value={phone}
                      onChangeText={setPhone}
                      error={fieldErrors.phone}
                      disabled={pending}
                    />

                    {apiErrorMessage && (
                      <Alert severity="error" variant="outlined">
                        {apiErrorMessage}
                      </Alert>
                    )}

                    <Button type="submit" size="large" fullWidth disabled={pending}>
                      {pending ? t('claim.phone.sending') : t('claim.phone.send')}
                    </Button>
                  </Stack>
                </form>
              )}

              {step === 1 && (
                <form onSubmit={onSubmitOtp} noValidate>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('claim.verify.body', { phone: `+27${phone}` })}
                    </Typography>
                    <Box>
                      <OtpInput
                        value={code}
                        onChange={setCode}
                        length={5}
                        error={!!fieldErrors.code}
                        disabled={pending}
                        aria-label={t('claim.verify.label')}
                      />
                      {fieldErrors.code && (
                        <Box sx={{ mt: 1 }}>
                          <Text variant="body-small" color="error.main">
                            {fieldErrors.code}
                          </Text>
                        </Box>
                      )}
                    </Box>

                    {apiErrorMessage && (
                      <Alert severity="error" variant="outlined">
                        {apiErrorMessage}
                      </Alert>
                    )}

                    <Button type="submit" size="large" fullWidth disabled={pending}>
                      {pending ? t('claim.verify.verifying') : t('claim.verify.verify')}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={pending}
                      onClick={() => {
                        setApiErrorMessage(null);
                        setFieldErrors({});
                        setCode('');
                        setStep(0);
                      }}
                    >
                      {t('claim.verify.useDifferent')}
                    </Button>
                  </Stack>
                </form>
              )}

              {step === 2 && (
                <form onSubmit={onSubmitPin} noValidate>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('claim.setPin.body')}
                    </Typography>
                    <TextField
                      label={t('claim.nameLabel')}
                      helperText={t('claim.nameHint')}
                      autoComplete="name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      disabled={pending}
                    />
                    <TextField
                      label={t('claim.setPin.newPin')}
                      type="password"
                      autoComplete="new-password"
                      inputMode="numeric"
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      error={!!fieldErrors.pin}
                      helperText={fieldErrors.pin}
                      disabled={pending}
                    />
                    <TextField
                      label={t('claim.setPin.confirmPin')}
                      type="password"
                      autoComplete="new-password"
                      inputMode="numeric"
                      value={confirmPin}
                      onChange={(e) => setConfirmPin(e.target.value)}
                      error={!!fieldErrors.confirmPin}
                      helperText={fieldErrors.confirmPin}
                      disabled={pending}
                    />

                    {apiErrorMessage && (
                      <Alert severity="error" variant="outlined">
                        {apiErrorMessage}
                      </Alert>
                    )}

                    <Button type="submit" size="large" fullWidth disabled={pending}>
                      {pending ? t('claim.setPin.claiming') : t('claim.setPin.claim')}
                    </Button>
                  </Stack>
                </form>
              )}
            </CardContent>
          </Card>

          <Stack spacing={1} sx={{ textAlign: 'center' }}>
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Typography variant="body2" color="primary">
                {t('claim.footer.backToSignIn')}
              </Typography>
            </Link>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
